/**
 * Reports › Fresh in / out (TASK-0259, FRESH-1; the spec's Step-1 report by mass balance). Bought against
 * sold, by family or by department, per week or month. The coverage callout comes BEFORE the numbers (the
 * prototype's rule: caveats first). Unexplained is stock, trim and loss together: never shrink. Period margin
 * (family view only) is this period's sales less this period's purchases, not cost of goods sold.
 */
import { Info } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api/client';
import { money, num, percent, qty } from '@/lib/format';
import type { FreshInOutGroup, FreshInOutReport, FreshInOutRow } from '@/lib/api/types';
import { Callout } from '@/ui/Callout';
import { ChipGroup } from '@/ui/Filters';
import { MultiSelect } from '@/ui/MultiSelect';
import { ErrorState, Panel, Skeleton, Stat, StatRow } from '@/ui/Panel';
import { useFamilies } from '@/features/catalog/families';

export const FRESH_GROUPS = ['family', 'department'] as const;
export type FreshGroupBy = (typeof FRESH_GROUPS)[number];

export const FRESH_IN_OUT_SUBTITLE =
  'Bought against sold, by family. Unexplained is stock, trim and loss together: not shrink.';

export function useFreshInOut(args: {
  from: string;
  to: string;
  groupBy: FreshGroupBy;
  family: string;
}) {
  return useQuery({
    queryKey: ['reports', 'fresh-in-out', args] as const,
    queryFn: () =>
      api.get<FreshInOutReport>('/reports/fresh-in-out', {
        params: {
          from: args.from,
          to: args.to,
          groupBy: args.groupBy,
          ...(args.family ? { family: args.family } : {}),
        },
      }),
  });
}

/** The coverage lines of one row, as the operator reads them. Empty when nothing is left out. */
export function coverageLines(
  row: FreshInOutRow,
  notTracked: { notTracked: number; total: number }
): string[] {
  const c = row.coverage;
  const out: string[] = [];
  if (c.boughtNoCostLines > 0)
    out.push(`cost not known: ${num(c.boughtNoCostLines)} bought lines (in lb only)`);
  if (c.boughtEaLines > 0)
    out.push(
      `each-unit bought lines: ${num(c.boughtEaLines)}, ${money(c.boughtEaCents)} (in $ only)`
    );
  if (c.soldEaLines > 0)
    out.push(
      `each-unit sold lines: ${num(c.soldEaLines)}, ${money(c.soldEaCents)} (in $ only)`
    );
  if (c.soldMissingWeightLines > 0)
    out.push(`missing weight: ${num(c.soldMissingWeightLines)} sold lines`);
  if (c.wasteEaLines > 0)
    out.push(`each-unit waste lines: ${num(c.wasteEaLines)} (not in lb)`);
  if (notTracked.notTracked > 0) {
    out.push(
      `waste cannot be recorded for a not-tracked item: ${num(notTracked.notTracked)} of ${num(notTracked.total)} products here are not tracked`
    );
  }
  if (row.beforeReceiptEvents)
    out.push('before receipt events: bought is not measured here');
  return out;
}

export function FreshInOutView({
  from,
  to,
  groupBy,
  family,
  onGroupBy,
  onFamily,
}: {
  from: string;
  to: string;
  groupBy: FreshGroupBy;
  family: string;
  onGroupBy: (g: FreshGroupBy) => void;
  onFamily: (csv: string) => void;
}) {
  const q = useFreshInOut({ from, to, groupBy, family });
  const families = useFamilies();
  const data = q.data;
  const familyOptions = (families.data ?? []).map((f) => ({
    value: f.code,
    label: f.name,
    hint: f.active ? undefined : 'retired',
  }));
  return (
    <>
      <Callout
        tone="neutral"
        icon={<Info />}
        title="Read the coverage before the numbers"
      >
        Bought is what arrived (receipt events) and sold is completed sales net of
        returns, each in the period. Unexplained is bought lb less sold lb and recorded
        waste lb: stock on hand, trim and loss together. It is not shrink: no count was
        taken at either end of the period.
        {data ? ` ${data.coverage.wasteNotRecordable}` : ''}
        {data && data.coverage.notAttributed.lines > 0
          ? ` Not attributed to any product: ${num(data.coverage.notAttributed.lines)} sold lines, ${money(data.coverage.notAttributed.cents)}.`
          : ''}
        {data?.coverage.noFamilyBought && data.coverage.noFamilyBought.lines > 0
          ? ` No family: ${num(data.coverage.noFamilyBought.lines)} bought lines, ${qty(data.coverage.noFamilyBought.lb)} lb, ${money(data.coverage.noFamilyBought.cents)} (not in any family's number).`
          : ''}
      </Callout>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-2xs text-ink-faint tracking-wider uppercase">By</span>
        <ChipGroup
          value={groupBy}
          onChange={onGroupBy}
          options={[
            { value: 'family', label: 'Family' },
            { value: 'department', label: 'Department' },
          ]}
        />
        <MultiSelect
          label="Family"
          loading={families.isLoading}
          options={familyOptions}
          selected={family ? family.split(',').filter(Boolean) : []}
          onChange={(next) => onFamily(next.join(','))}
        />
        {data ? (
          <span className="text-2xs text-ink-faint">
            {data.bucket === 'week' ? 'By week (Mondays)' : 'By month'}
          </span>
        ) : null}
      </div>
      {q.isError ? (
        <ErrorState
          title="The report did not load"
          message={errorMessage(q.error)}
          onRetry={() => void q.refetch()}
        />
      ) : !data ? (
        <Skeleton className="h-24" />
      ) : (
        <>
          <StatRow className={groupBy === 'family' ? 'xl:grid-cols-6' : 'xl:grid-cols-5'}>
            <Stat
              label="Bought"
              value={`${qty(data.totals.boughtLb)} lb`}
              sub={money(data.totals.boughtCents)}
            />
            <Stat
              label="Sold"
              value={`${qty(data.totals.soldLb)} lb`}
              sub={money(data.totals.soldCents)}
            />
            <Stat label="Waste" value={`${qty(data.totals.wasteLb)} lb`} sub="recorded" />
            <Stat
              label="Unexplained"
              value={`${qty(data.totals.unexplainedLb)} lb`}
              sub="stock, trim and loss; not shrink"
              tone={data.totals.unexplainedLb < 0 ? 'warn' : undefined}
            />
            <Stat
              label="Products"
              value={num(data.groups.reduce((n, g) => n + g.products.total, 0))}
              sub={`${num(data.groups.reduce((n, g) => n + g.products.inputs, 0))} inputs`}
            />
            {groupBy === 'family' ? (
              <Stat
                label="Period margin"
                value={
                  data.totals.periodMarginPct === null
                    ? '—'
                    : percent(data.totals.periodMarginPct)
                }
                sub="sales less purchases this period, not cost of goods sold"
                tone={
                  data.totals.periodMarginPct !== null && data.totals.periodMarginPct < 0
                    ? 'negative'
                    : undefined
                }
              />
            ) : null}
          </StatRow>
          {data.groups.length === 0 ? (
            <p className="text-2xs text-ink-faint">
              Nothing bought, sold or wasted in the period for these{' '}
              {groupBy === 'family' ? 'families' : 'departments'}.
            </p>
          ) : (
            data.groups.map((g) => <GroupTable key={g.key} group={g} groupBy={groupBy} />)
          )}
        </>
      )}
    </>
  );
}

function GroupTable({
  group,
  groupBy,
}: {
  group: FreshInOutGroup;
  groupBy: FreshGroupBy;
}) {
  const withMargin = groupBy === 'family';
  const description = [
    group.measurementStartsOn
      ? `Receipts recorded from ${group.measurementStartsOn}`
      : 'No receipt recorded yet',
    `${num(group.products.notTracked)} of ${num(group.products.total)} products not tracked`,
    group.noFamilyBought && group.noFamilyBought.lines > 0
      ? `no family: ${num(group.noFamilyBought.lines)} bought lines, ${qty(group.noFamilyBought.lb)} lb, ${money(group.noFamilyBought.cents)}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Panel title={group.label} description={description} bleed>
      <table className="w-full text-xs" data-testid={`fresh-in-out-${group.key}`}>
        <thead>
          <tr className="border-line bg-surface-sunken text-2xs text-ink-faint border-b tracking-wider uppercase">
            <th className="px-3 py-1.5 text-left font-medium">Period</th>
            <th className="w-24 px-3 py-1.5 text-right font-medium">Bought lb</th>
            <th className="w-24 px-3 py-1.5 text-right font-medium">Bought $</th>
            <th className="w-24 px-3 py-1.5 text-right font-medium">Sold lb</th>
            <th className="w-24 px-3 py-1.5 text-right font-medium">Sold $</th>
            <th className="w-24 px-3 py-1.5 text-right font-medium">Waste lb</th>
            <th className="w-28 px-3 py-1.5 text-right font-medium">Unexplained lb</th>
            {withMargin ? (
              <th className="w-28 px-3 py-1.5 text-right font-medium">Period margin</th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {[...group.rows, group.totals].map((r) => {
            const lines = coverageLines(r, group.products);
            const isTotal = r.bucket === 'total';
            return (
              <tr
                key={r.bucket}
                className={`border-line-soft border-b last:border-0 ${isTotal ? 'bg-surface-sunken font-semibold' : ''} ${r.beforeReceiptEvents ? 'text-ink-faint' : ''}`}
              >
                <td className="px-3 py-1.5">
                  <div>{isTotal ? 'Period total' : r.bucket}</div>
                  {lines.length > 0 ? (
                    <div className="text-2xs text-ink-faint font-normal">
                      {lines.join(' · ')}
                    </div>
                  ) : null}
                </td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                  {qty(r.boughtLb)}
                </td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                  {money(r.boughtCents)}
                </td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                  {qty(r.soldLb)}
                </td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                  {money(r.soldCents)}
                </td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                  {qty(r.wasteLb)}
                </td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                  {qty(r.unexplainedLb)}
                </td>
                {withMargin ? (
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                    {r.periodMarginPct === null ? (
                      <span className="text-ink-faint">—</span>
                    ) : (
                      <span
                        className={r.periodMarginPct < 0 ? 'text-negative' : undefined}
                      >
                        {percent(r.periodMarginPct)}
                      </span>
                    )}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </Panel>
  );
}
